import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { RootState } from 'actions';
import { EmploymentType, GetEmploymentResponseDto, posEmploymentsApi } from 'api/SmallLoanProgram/EmploymentsApi';
import Loader from 'components/Loader/index';
import { BorrowerRole } from 'enums/BorrowerRole';
import { DateFormat } from 'enums/DateFormat';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';
import { getDateFromFormat } from 'utils/dateUtils';
import SubSection from 'components/LoanPortal/Sections/SubSection';
import { Button } from 'components/Buttons/Button';

import EmploymentFormRow from './EmploymentFormRow/EmploymentFormRow';
import {
  EmploymentStatusName,
  PreviousEmploymentFieldNames,
  PrimaryEmploymentFieldNames,
} from './employmentInformationBlock.enums';
import styles from './EmploymentInformationBlock.module.scss';
import { EmploymentForm } from './employmentInformationBlock.types';
import {
  getPreviousEmploymentFieldsSettings,
  getPrimaryEmploymentFieldsSettings,
} from './employmentInformationBlock.utils';

const getMaxPreviousHireDate = (dateString: string) => {
  return getDateFromFormat(dateString, DateFormat.MonthYear).toDate();
};

interface EmploymentInformationBlockProps {
  borrowerRole: BorrowerRole;
}

const FORM_ID = 'employment';

dayjs.extend(customParseFormat);

const EmploymentInformationBlock = ({ borrowerRole }: EmploymentInformationBlockProps) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { applicationId } = useAppSelector((state) => state.application);
  const { borrowerId } = useSelector((state: RootState) => state.borrower);
  const { isInformationSaved, firstName } = useAppSelector((state) => {
    const { borrowerEmploymentInformationComplete, coBorrowerEmploymentInformationComplete } = state.dashboardPortal;
    const { firstName: borrowerFirstName } = state.borrower;
    const { firstName: coborrowerFirstName } = state.coBorrower;

    if (borrowerRole === BorrowerRole.Borrower) {
      return {
        isInformationSaved: borrowerEmploymentInformationComplete,
        firstName: borrowerFirstName,
      };
    }

    return {
      isInformationSaved: coBorrowerEmploymentInformationComplete,
      firstName: coborrowerFirstName,
    };
  });
  const [primaryEmployment, setPrimaryEmployment] = useState<GetEmploymentResponseDto | null>(null);
  const { register, trigger, handleSubmit, watch, formState, setValue, clearErrors, ...methods } =
    useForm<EmploymentForm>({
      defaultValues: {
        [PrimaryEmploymentFieldNames.BusinessName]: '',
        [PrimaryEmploymentFieldNames.Position]: '',
        [PrimaryEmploymentFieldNames.HireDate]: '',
        [PreviousEmploymentFieldNames.BusinessName]: '',
        [PreviousEmploymentFieldNames.Position]: '',
        [PreviousEmploymentFieldNames.HireDate]: '',
      },
      shouldFocusError: true,
      reValidateMode: 'onChange',
    });
  const watcher = watch();

  const checkIsPreviousEmploymentRequired = () => {
    const hireDateField = watcher[PrimaryEmploymentFieldNames.HireDate];

    if (!hireDateField) {
      return false;
    }

    const hireDate = dayjs(hireDateField, DateFormat.MonthYear);

    if (!hireDate.isValid()) {
      return false;
    }

    const twoYearsAgo = dayjs().subtract(2, 'year');

    return hireDate.isAfter(twoYearsAgo);
  };

  const getEmploymentFillingInformation = async () => {
    try {
      setIsLoading(true);

      const employments = await posEmploymentsApi.getEmployments({
        applicationId: applicationId!,
        borrowerRole: borrowerRole,
      });

      const primaryEmploymentInfo = employments.find((employment) => employment.type === EmploymentType.Primary);
      const previousEmploymentInfo = employments.find((employment) => employment.type === EmploymentType.Previous);

      if (primaryEmploymentInfo) {
        setPrimaryEmployment(primaryEmploymentInfo!);
        setValue(PrimaryEmploymentFieldNames.BusinessName, primaryEmploymentInfo.businessName || '');
        setValue(PrimaryEmploymentFieldNames.Position, primaryEmploymentInfo.position || '');
        setValue(PrimaryEmploymentFieldNames.HireDate, primaryEmploymentInfo.hireDate || '');
      }

      if (previousEmploymentInfo) {
        setValue(PreviousEmploymentFieldNames.BusinessName, previousEmploymentInfo.businessName || '');
        setValue(PreviousEmploymentFieldNames.Position, previousEmploymentInfo.position || '');
        setValue(PreviousEmploymentFieldNames.HireDate, previousEmploymentInfo.hireDate || '');
      }

      setIsLoading(false);
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    }
  };

  useEffect(() => {
    getEmploymentFillingInformation();
  }, [applicationId, borrowerRole, dispatch, setValue]);

  const onSubmit = handleSubmit(async () => {
    try {
      setIsLoading(true);

      await posEmploymentsApi.updateEmployments({
        applicationId: applicationId!,
        borrowerRole: borrowerRole,
        primaryEmployment: {
          businessName: watcher[PrimaryEmploymentFieldNames.BusinessName],
          position: watcher[PrimaryEmploymentFieldNames.Position],
          hireDate: dayjs(watcher[PrimaryEmploymentFieldNames.HireDate], DateFormat.MonthYear).toDate(),
        },
        previousEmployment: checkIsPreviousEmploymentRequired()
          ? {
              businessName: watcher[PreviousEmploymentFieldNames.BusinessName],
              position: watcher[PreviousEmploymentFieldNames.Position],
              hireDate: dayjs(watcher[PreviousEmploymentFieldNames.HireDate], DateFormat.MonthYear).toDate(),
            }
          : null,
      });

      await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      await getEmploymentFillingInformation();
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <>
      {isLoading ? (
        <Loader customContainerClassname={styles.loader} />
      ) : (
        <div className={styles.requiredInformationContainer}>
          <SubSection
            state={{
              isExist: true,
              isCompleted: isInformationSaved,
            }}
          >
            {!!primaryEmployment!.status && (
              <FormProvider
                register={register}
                trigger={trigger}
                handleSubmit={handleSubmit}
                formState={formState}
                watch={watch}
                setValue={setValue}
                clearErrors={clearErrors}
                {...methods}
              >
                <form className={styles.form} id={FORM_ID} onSubmit={onSubmit}>
                  <div className={styles.title}>
                    Please Complete The Information About {firstName}'s Primary Source Of Income
                  </div>
                  <EmploymentFormRow
                    title={EmploymentStatusName[primaryEmployment!.status]}
                    fieldNames={PrimaryEmploymentFieldNames}
                    fieldsSettings={getPrimaryEmploymentFieldsSettings(primaryEmployment!.status, isInformationSaved)}
                  />
                  {checkIsPreviousEmploymentRequired() && (
                    <EmploymentFormRow
                      title="Please Provide Information On Your Previous Employment"
                      fieldNames={PreviousEmploymentFieldNames}
                      fieldsSettings={getPreviousEmploymentFieldsSettings(isInformationSaved)}
                      maxHireDate={getMaxPreviousHireDate(watcher[PrimaryEmploymentFieldNames.HireDate])}
                    />
                  )}
                  {!isInformationSaved && (
                    <Button
                      className={styles.submitButton}
                      disabled={isInformationSaved || isLoading}
                      onClick={onSubmit}
                    >
                      Save
                    </Button>
                  )}
                </form>
              </FormProvider>
            )}
          </SubSection>
        </div>
      )}
    </>
  );
};

export default EmploymentInformationBlock;
